import {
    AlertTriangle,
    ArrowRight,
    BarChart3,
    ClipboardCheck,
    Fingerprint,
    HardHat,
    Users,
    Wrench,
} from "lucide-react";
import {
    ModuleCard,
    SectionEyebrow,
    SignatureCard,
} from "@/components/Landing/primitives";

const MODULES = Object.freeze([
    {
        id: "workforce",
        icon: HardHat,
        title: "Workforce compliance",
        body: "Tickets, licences, medicals, inductions, fit-for-work. AI-classified at upload. Auto-blocked at site entry on lapse.",
    },
    {
        id: "plant",
        icon: Wrench,
        title: "Plant and equipment",
        body: "Asset register, services, inspections, prestart checks, fault reporting, lockout-tagout. Linked to verified operator.",
    },
    {
        id: "subcontractors",
        icon: Users,
        title: "Subcontractor management",
        body: "Prequalification, insurance, SWMS review, capability assessment, scope alignment, performance scoring.",
    },
    {
        id: "site-access",
        icon: Fingerprint,
        title: "Site access control",
        body: "QR or biometric kiosks. Real-time on-site headcount. Mustering and evacuation reports in seconds.",
    },
    {
        id: "permits",
        icon: ClipboardCheck,
        title: "Permits and high-risk work",
        body: "Permits to work, hot work, confined space, working at heights, lifting studies. Issued, signed, closed-out digitally.",
    },
    {
        id: "incidents",
        icon: AlertTriangle,
        title: "Incident, hazard, and audit",
        body: "Incident reporting with ICAM workflow, hazard reports, toolbox talks, audits, corrective actions tracked to closure.",
    },
    {
        id: "sentinel",
        icon: BarChart3,
        title: "SENTINEL Safety Intelligence Dashboard",
        body: "Analytics layer across every module. Hero scorecard. Nine RAG indicators. Contractor letter grades. Live critical risk control verification.",
        flagship: true,
    },
]);

export default function Modules({ onBook }) {
    return (
        <section
            id="platform"
            className="relative py-24 sm:py-32 bg-white text-[#0A0A0A] border-t border-zinc-200"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl reveal">
                    <SectionEyebrow>Platform modules</SectionEyebrow>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1] mt-3">
                        Built around how Tier 1 projects actually run.
                    </h2>
                </div>

                <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {MODULES.map((m) => (
                        <ModuleCard
                            key={m.id}
                            icon={m.icon}
                            title={m.title}
                            body={m.body}
                            flagship={m.flagship}
                        />
                    ))}
                    <SignatureCard
                        title="One contract."
                        stat="ONE"
                        kicker="security review"
                        body="Workforce, plant, subcontractors, permits, incidents, audits, and intelligence on a single schema."
                    />
                </div>

                <div className="mt-12 reveal">
                    <div className="rounded-2xl bg-[#0A0A0A] text-white p-5 sm:p-6 flex flex-col sm:flex-row items-center gap-4 sm:gap-6 justify-between">
                        <p className="font-display text-base sm:text-lg uppercase tracking-[0.18em] text-center sm:text-left">
                            One contract{" "}
                            <span className="text-[#1FA8FF]">·</span> one
                            security review{" "}
                            <span className="text-[#1FA8FF]">·</span> one
                            source of truth for compliance, safety, and
                            workforce data
                        </p>
                        <button
                            onClick={onBook}
                            className="shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#1FA8FF] text-white font-semibold hover:bg-[#0C84D4]"
                        >
                            Book a demo <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}
